import { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { documentationRootGuid } from '@constants'
import { difference, intersection } from 'lodash-es'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useAvailableTemplates, useProjectFiles } from '@editorHooks'
import StructurePreview from './components/StructurePreview'
import TemplateSelection from './components/TemplateSelection'
import {
  StructurePreviewNode,
  buildStructurePreview,
  collectSelectedTemplates,
  getTemplateSubtreeGuids,
} from './utils'

interface Props {
  open: boolean
  onClose: () => void
  isAddingTemplates: boolean
  onAddTemplates: (keys: TemplatesResult) => Promise<unknown>
  alreadyAddedTemplates: string[]
}

const AddTemplateModal = ({
  open,
  onClose,
  isAddingTemplates,
  onAddTemplates,
  alreadyAddedTemplates,
}: Props) => {
  const { data: availableTemplates, isLoading, isFetching } = useAvailableTemplates()
  const { data: documentTree, isLoading: isLoadingDocumentTree } =
    useProjectFiles(documentationRootGuid)

  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([])
  // The preview at the time of saving: shown until the dialog closes, as the
  // reloaded structure already contains (and no longer highlights) the new items
  const [savingPreview, setSavingPreview] = useState<StructurePreviewNode[] | null>(null)

  const allTemplates = useMemo(
    () => Object.values(availableTemplates ?? {}).flatMap(category => category.templates),
    [availableTemplates],
  )

  const templatesToAdd = useMemo(
    // Already added templates are added again when selected (the preview marks them)
    () => collectSelectedTemplates(allTemplates, new Set(selectedTemplates)),
    [allTemplates, selectedTemplates],
  )

  // Rendering the preview of a large structure takes a while: the selection
  // updates right away, the preview follows in a deferred render
  const previewedTemplates = useDeferredValue(templatesToAdd)
  const isUpdatingPreview = previewedTemplates !== templatesToAdd

  const readdedTemplateCount = useMemo(
    () => intersection(selectedTemplates, alreadyAddedTemplates).length,
    [selectedTemplates, alreadyAddedTemplates],
  )

  const structurePreview = useMemo(
    () =>
      buildStructurePreview(
        documentTree.files,
        documentTree.sections,
        documentationRootGuid,
        previewedTemplates,
      ),
    [documentTree.files, documentTree.sections, previewedTemplates],
  )

  // Removing a new item removes everything the save would create below it
  const removeTemplate = useCallback(
    (templateGuid: string) =>
      setSelectedTemplates(selected =>
        difference(selected, getTemplateSubtreeGuids(allTemplates, templateGuid)),
      ),
    [allTemplates],
  )

  const close = () => {
    if (isAddingTemplates) return
    setSelectedTemplates([])
    onClose()
  }

  const save = async () => {
    // The preview of the current selection, even if it is still being updated
    setSavingPreview(
      buildStructurePreview(
        documentTree.files,
        documentTree.sections,
        documentationRootGuid,
        templatesToAdd,
      ),
    )
    try {
      await onAddTemplates({ templates: templatesToAdd })
      setSelectedTemplates([])
      onClose()
    } catch {
      // Errors are handled by the mutation's onError
    } finally {
      setSavingPreview(null)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth data-cy="add-template-modal">
      <DialogTitle>Template hinzufügen</DialogTitle>
      <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
        {isAddingTemplates && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
        )}
        <Stack
          direction="row"
          sx={{
            height: '70vh',
            // No selection changes while the templates are being added
            pointerEvents: isAddingTemplates ? 'none' : undefined,
            opacity: isAddingTemplates ? 0.6 : undefined,
          }}
        >
          <Stack sx={{ flex: 1, minWidth: 0, p: 2 }} spacing={1}>
            <Typography variant="subtitle2">Templates</Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <TemplateSelection
                availableTemplates={availableTemplates}
                // Background refetches (e.g. after saving) keep showing the loaded templates
                isLoading={isLoading || (isFetching && !availableTemplates)}
                alreadyAddedTemplates={alreadyAddedTemplates}
                selectedTemplates={selectedTemplates}
                onChangeSelectedTemplates={setSelectedTemplates}
              />
            </Box>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack sx={{ flex: 1, minWidth: 0, p: 2 }} spacing={1}>
            <Typography variant="subtitle2">Neue Struktur der Dokumentation</Typography>
            <Typography variant="caption" color="text.secondary">
              Neue Einträge sind farbig hervorgehoben und können wieder entfernt werden.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <Box
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  opacity: isUpdatingPreview && !savingPreview ? 0.5 : undefined,
                }}
              >
                <StructurePreview
                  nodes={savingPreview ?? structurePreview}
                  alreadyAddedTemplates={alreadyAddedTemplates}
                  onRemoveTemplate={removeTemplate}
                />
              </Box>
              {isUpdatingPreview && !savingPreview && (
                <Box
                  data-cy="template-structure-preview-updating"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        {isAddingTemplates ? (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            Templates werden hinzugefügt …
          </Typography>
        ) : (
          readdedTemplateCount > 0 && (
            <Typography
              variant="body2"
              color="warning.main"
              sx={{ mr: 'auto', pl: 1 }}
              data-cy="template-readded-hint"
            >
              {readdedTemplateCount === 1
                ? '1 bereits vorhandenes Template wird erneut hinzugefügt.'
                : `${readdedTemplateCount} bereits vorhandene Templates werden erneut hinzugefügt.`}
            </Typography>
          )
        )}
        <Button size="small" onClick={close} disabled={isAddingTemplates}>
          Abbrechen
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          onClick={save}
          loading={isAddingTemplates}
          disabled={templatesToAdd.length === 0 || isLoadingDocumentTree}
          data-cy="btn-save-templates"
        >
          Speichern
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default AddTemplateModal
